import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { IOption, Select, Tooltip } from '@gcl/react-core';
import { QueryKey } from '@tanstack/react-query';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { IProjectTransmittalItemRevision } from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import { useSiteTransmittalIssueRevision } from './transmittalItemTableForm.hooks';

interface ITransmittalItemTableRowIssueableSelectProps {
  item: IFormFileDrawingItem;
  masterScheduleId: number;
  transmittalId: number;
  docType: IProjectTransmittalItemDocumentType;
  transmittalType?: ETransmittalType;
  isAnyActionLoading: boolean;
  setRowUploading: (index: number, isUploading: boolean) => void;
  index: number;
  isRefetchingTransmittalItems: boolean;
  queryKey: QueryKey;
}

export const TransmittalItemTableRowIssueableSelect = ({
  item,
  masterScheduleId,
  transmittalId,
  transmittalType,
  isAnyActionLoading,
  setRowUploading,
  index,
  isRefetchingTransmittalItems,
  queryKey,
  docType,
}: ITransmittalItemTableRowIssueableSelectProps) => {
  const {
    dropdownOptions,
    setLocalValue,
    localValue,
    optionFromItem,
    onMutate,
    mutationUpsertTransmittalItemsAndRevisions,
    isLatestRevision,
    latestRevisionId,
  } = useSiteTransmittalIssueRevision(masterScheduleId, transmittalId, index, item, docType, setRowUploading);

  const {
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  if (!item.transmittalItem?.inUse) {
    return <div className="gcl-fb-sm">Not Issue</div>;
  }

  return (
    <div className="gcl-gems-transmittal-item-issuable-column">
      <ApiErrorDisplay
        errors={[
          {
            error: errorUpsertTransmittalItemsAndRevisions,
            genericError: 'Update Transmittal revision failed',
            reset: resetUpsertTransmittalItemsAndRevisions,
          },
        ]}
        callbackAfterToast={() => {
          setRowUploading(index, false);
          setLocalValue(optionFromItem);
        }}
      />
      <Select
        className="gcl-gems-transmittal-item-issuable-select"
        id={`${item.transmittalItem.id || '0'}_issue_select`}
        onChange={(newValue: IOption<IProjectTransmittalItemRevision | undefined>[]) => {
          if (newValue.length > 0) {
            setLocalValue(newValue[0]);
            onMutate(newValue[0]);
          }
        }}
        renderMenuItem={(revision) => {
          return (
            <>
              {revision.text}
              {revision.data?.id === latestRevisionId && ' (Latest)'}
            </>
          );
        }}
        value={localValue ? [localValue] : undefined}
        options={dropdownOptions}
        allowSearch={false}
        clearable={false}
        small
        selectProps={{
          positionFixed: true,
        }}
        disabled={isRefetchingTransmittalItems || isLoadingUpsertTransmittalItemsAndRevisions}
      />
      {!isLatestRevision && (
        <Tooltip overlay="New revision available">
          <div className="gcl-gems-transmittal-item-issuable-not-latest " />
        </Tooltip>
      )}
    </div>
  );
};
